import { useState, useMemo, useRef } from "react";
import Table from "views/merchandise/tables/BulkActionsTable";
import { useDates } from "dates/DatesContext";
import { formatDatamartFilters } from "utils/formatV2DatamartFilters";
import { useFetch } from "hooks/api";
import moment from "moment";
import { getDSPMetricSpec } from "./metrics";
import { useAppSelector } from "redux/hooks";
import { Col, Row } from "react-bootstrap";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import { mergeTrendData } from "utils/mergeTrendData";
import Loading from "components/core/blocks/Loading";
import LocationChart from "./LocationChart";
import useDSPSellerInfo from "./hooks/useDSPSellerInfo";

const mapRegion = (country, region) => {
  if (country === "United States") {
    switch (region) {
      case "Alabama":
        return "us-al";
      case "Alaska":
        return "us-ak";
      case "Arizona":
        return "us-az";
      case "Arkansas":
        return "us-ar";
      case "California":
        return "us-ca";
      case "Colorado":
        return "us-co";
      case "Connecticut":
        return "us-ct";
      case "Delaware":
        return "us-de";
      case "Florida":
        return "us-fl";
      case "Georgia":
        return "us-ga";
      case "Hawaii":
        return "us-hi";
      case "Idaho":
        return "us-id";
      case "Illinois":
        return "us-il";
      case "Indiana":
        return "us-in";
      case "Iowa":
        return "us-ia";
      case "Kansas":
        return "us-ks";
      case "Kentucky":
        return "us-ky";
      case "Louisiana":
        return "us-la";
      case "Maine":
        return "us-me";
      case "Maryland":
        return "us-md";
      case "Massachusetts":
        return "us-ma";
      case "Michigan":
        return "us-mi";
      case "Minnesota":
        return "us-mn";
      case "Mississippi":
        return "us-ms";
      case "Missouri":
        return "us-mo";
      case "Montana":
        return "us-mt";
      case "Nebraska":
        return "us-ne";
      case "Nevada":
        return "us-nv";
      case "New Hampshire":
        return "us-nh";
      case "New Jersey":
        return "us-nj";
      case "New Mexico":
        return "us-nm";
      case "New York":
        return "us-ny";
      case "North Carolina":
        return "us-nc";
      case "North Dakota":
        return "us-nd";
      case "Ohio":
        return "us-oh";
      case "Oklahoma":
        return "us-ok";
      case "Oregon":
        return "us-or";
      case "Pennsylvania":
        return "us-pa";
      case "Rhode Island":
        return "us-ri";
      case "South Carolina":
        return "us-sc";
      case "South Dakota":
        return "us-sd";
      case "Tennessee":
        return "us-tn";
      case "Texas":
        return "us-tx";
      case "Utah":
        return "us-ut";
      case "Vermont":
        return "us-vt";
      case "Virginia":
        return "us-va";
      case "Washington":
        return "us-wa";
      case "West Virginia":
        return "us-wv";
      case "Wisconsin":
        return "us-wi";
      case "Wyoming":
        return "us-wy";
      case "District of Columbia":
        return "us-dc";
      default:
        return null;
    }
  }
  return null;
};

const DSPGeoDashboard = ({ all = false, lineItemId = null, orderId = null }) => {
  const [selectedMetric, setSelectedMetric] = useState("dsp_total_detail_page_views");
  const marketplace = useAppSelector((state) => state.marketPlace);
  const { dspAdvertiser } = useDSPSellerInfo();
  const { start, end, preStart, preEnd } = useDates();
  const startDate = moment(start).format("YYYY-MM-DD");
  const endDate = moment(end).format("YYYY-MM-DD");
  const preStartDate = moment(preStart).format("YYYY-MM-DD");
  const preEndDate = moment(preEnd).format("YYYY-MM-DD");
  const tableRef = useRef();
  const TABLE_ID = "dspGeoTable";
  const filterProps = useCustomTableFilters(TABLE_ID);

  const dataFilters = useMemo(() => {
    const filters = {
      date__gte: startDate,
      date__lte: endDate,
      dsp_seller_info_id: dspAdvertiser?.id,
    };
    if (lineItemId) {
      filters.line_item_id = lineItemId;
    }
    if (orderId) {
      filters.order_id = orderId;
    }
    return filters;
  }, [dspAdvertiser, startDate, endDate, lineItemId, orderId]);

  const { data: dspGeoData, isLoading: isDSPGeoDataLoading } = useFetch(
    ["dspGeoData", "region", startDate, endDate, all, orderId, lineItemId],
    "/api/data_report_v2/dspgeo/stats/",
    {
      dimensions: "country,region",
      filters: formatDatamartFilters(dataFilters),
    },
    {
      default: [],
      select: (res) => {
        return res.data;
      },
      staleTime: 5 * 60 * 1000,
      enabled: !!dspAdvertiser || !!lineItemId || !!orderId || all,
    },
  );

  const { data: dspGeoDataPrev } = useFetch(
    ["dspGeoDataPrev", "region", preStartDate, preEndDate, all, orderId, lineItemId],
    "/api/data_report_v2/dspgeo/stats/",
    {
      dimensions: "country,region",
      filters: formatDatamartFilters({
        ...dataFilters,
        date__gte: preStartDate,
        date__lte: preEndDate,
      }),
    },
    {
      default: [],
      select: (res) => {
        return res.data;
      },
      staleTime: 5 * 60 * 1000,
      enabled: !!dspAdvertiser || !!lineItemId || !!orderId || all,
    },
  );

  const mergedData = useMemo(() => {
    return mergeTrendData(dspGeoData, dspGeoDataPrev, "region", (d) => d.region + ", " + d.country);
  }, [dspGeoData, dspGeoDataPrev]);

  const metric = useMemo(
    () => getDSPMetricSpec(marketplace.marketPlace, selectedMetric),
    [marketplace, selectedMetric],
  );

  const geoData = useMemo(() => {
    if (!dspGeoData) {
      return [];
    }
    return dspGeoData
      .map((d) => {
        const state = mapRegion(d.country, d.region);
        if (!state) {
          return null;
        }
        return {
          "hc-key": state,
          value: metric.accessor(d) || 0,
        };
      })
      .filter((d) => d !== null);
  }, [dspGeoData, metric]);

  const metricOptions = [
    { value: "dsp_total_detail_page_views", label: "Detail Page Views" },
    { value: "dsp_total_add_to_cart", label: "Add to Carts" },
    { value: "dsp_total_new_to_brand_dpvs", label: "New to Brand DPV" },
    { value: "dsp_total_units", label: "Total Units" },
  ];

  const regionData = useMemo(() => {
    if (!dspGeoData) {
      return [];
    }
    return dspGeoData.map((d) => {
      const region = (d.region || "Other") + ", " + d.country;
      return {
        ...d,
        region,
      };
    }, {});
  }, [dspGeoData]);

  const columnSpecs = useMemo(() => {
    return [
      { key: "country", compareKey: null, options: { metric: { id: "country", format: "text", name: "Country" } } },
      { key: "dsp_total_detail_page_views", compareKey: "dsp_total_detail_page_views_percent" },
      { key: "dsp_total_add_to_cart", compareKey: "dsp_total_add_to_cart_percent" },
      { key: "dsp_total_new_to_brand_dpvs", compareKey: "dsp_total_new_to_brand_dpvs_percent" },
      { key: "dsp_total_units", compareKey: "dsp_total_units_percent" },
    ];
  }, []);

  if (isDSPGeoDataLoading) {
    return <Loading height={"300px"} fullPage={true} />;
  }

  return (
    <>
      <LocationChart
        regionData={regionData}
        geoData={geoData}
        metricOptions={metricOptions}
        selectedMetric={selectedMetric}
        onSelectMetric={setSelectedMetric}
      />
      <Row>
        <Col xs={12}>
          <Table
            marketPlace={marketplace}
            tableRef={tableRef}
            tableId={TABLE_ID}
            isExpandable={false}
            isSelectable={false}
            titleCol={{
              id: "region",
              isStatic: true,
              Header: "Region",
              accessor: "region",
              format: "text",
              style: { width: "300px" },
              width: 300,
              Cell: ({ row }) => {
                return row.region || "Other";
              },
            }}
            data={mergedData}
            columnSpecs={columnSpecs}
            getSearchCriteria={(row) => {
              const { region, country } = row ?? {};
              return `${region} ${country}`;
            }}
            tableRowDescription={"Region"}
            defaultSorted={[{ id: "dsp_total_detail_page_views", desc: true }]}
            isLoading={isDSPGeoDataLoading}
            filterWidget={true}
            hideFilterToggleIcon={true}
            hideResetFiltersIcon={false}
            {...filterProps}
            handleBulkChanges={() => {}}
            minRows={5}
            selectionKey={"id"}
          />
        </Col>
      </Row>
    </>
  );
};

export default DSPGeoDashboard;
